import { isNil } from '#layers/base/public';

/**
 * Check if the event path contains elements that should skip overlay handling
 * (add-block controls or selection toolbars)
 */
export function pathHasOverlaySkip(path: Array<EventTarget>) {
	for (const el of path) {
		const node = el as HTMLElement | null;

		if (!isNil(node?.dataset?.blockEditorPreventSelection)) {
			return true;
		}
	}

	return false;
}

/**
 * Check whether an element allows direct text editing.
 */
function isEditableElement(node: HTMLElement) {
	const contentEditable = node.getAttribute('contenteditable');

	return node.isContentEditable === true
		|| (contentEditable !== null && contentEditable.toLowerCase() !== 'false');
}

/**
 * Check whether an element should own its pointer gesture inside an interactive zone.
 */
function elementOwnsInteractiveZoneGesture(node: HTMLElement) {
	return node.tabIndex >= 0 || isEditableElement(node);
}

/**
 * Check if a path starts from a gesture-owning element before it reaches an interactive-zone marker.
 */
export function pathHasGestureOwnerBeforeInteractiveZone(path: Array<EventTarget>) {
	let hasGestureOwner = false;

	for (const el of path) {
		const node = el as HTMLElement | null;

		if (typeof node?.getAttribute !== 'function') {
			continue;
		}

		if (!isNil(node.dataset?.blockEditorInteractiveZone)) {
			return hasGestureOwner || elementOwnsInteractiveZoneGesture(node);
		}

		if (elementOwnsInteractiveZoneGesture(node)) {
			hasGestureOwner = true;
		}
	}

	return false;
}

/**
 * Check if the event path contains interactive block content.
 */
export function pathHasInteractiveZone(path: Array<EventTarget>) {
	for (const el of path) {
		const node = el as HTMLElement | null;

		if (!isNil(node?.dataset?.blockEditorInteractiveZone)) {
			return true;
		}
	}

	return false;
}

/**
 * Check if the event path contains the block editor canvas element
 */
export function pathHasCanvas(path: Array<EventTarget>) {
	for (const el of path) {
		const node = el as HTMLElement | null;

		if (!isNil(node?.dataset?.blockEditorCanvas)) {
			return true;
		}
	}

	return false;
}
